import { fetchConfig } from '@theme/utilities';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';

/**
 * Costo di gestione automatico del carrello.
 *
 * Su Shopify il tema non può modificare il prezzo reale del carrello: per
 * addebitare davvero il costo si usa un prodotto "Costi di gestione" nascosto
 * (prezzo unitario basso, es. 0,01 €) di cui si regola la quantità.
 *
 * Il calcolo dipende dai metafield prodotto (custom.amazon_price), che NON sono
 * esposti in /cart.js: per questo la quantità desiderata della fee è calcolata
 * in Liquid (snippets/cart-products.liquid) ed esposta nel DOM come
 * data-surcharge-fee-qty. Questo componente si limita a leggerla e ad allineare
 * la quantità del prodotto-fee nel carrello.
 */
class CartSurchargeManager extends HTMLElement {
  #busy = false;

  connectedCallback() {
    this.config = {
      variantId: parseInt(this.dataset.variantId, 10) || 0,
      unitPrice: parseInt(this.dataset.unitPrice, 10) || 1, // centesimi per unità
    };

    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    document.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate);

    // Allineamento iniziale (es. cliente che torna con prodotti già nel carrello)
    this.scheduleSync();
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate);
  }

  handleCartUpdate(event) {
    // Ignora gli aggiornamenti generati da noi stessi per evitare loop
    if (event?.detail?.data?.source === 'cart-surcharge') return;
    this.scheduleSync();
  }

  // Aspetta il frame successivo così il morph delle sezioni carrello è completo
  // e data-surcharge-fee-qty riflette lo stato aggiornato.
  scheduleSync() {
    requestAnimationFrame(() => this.sync());
  }

  // Quantità desiderata della fee, calcolata in Liquid e letta dal DOM.
  readDesiredQty() {
    const el = document.querySelector('[data-surcharge-fee-qty]');
    if (!el) return null; // carrello non renderizzato in pagina
    const value = parseInt(el.dataset.surchargeFeeQty, 10);
    return Number.isFinite(value) ? value : 0;
  }

  async getCart() {
    const response = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
    });
    return response.json();
  }

  async sync() {
    if (this.#busy || !this.config.variantId) return;

    const desiredQty = this.readDesiredQty();
    if (desiredQty == null) return;

    this.#busy = true;
    try {
      const cart = await this.getCart();
      const feeItem = cart.items.find((item) => item.variant_id === this.config.variantId);
      const currentQty = feeItem ? feeItem.quantity : 0;
      if (desiredQty === currentQty) return;

      await this.applyChange(feeItem, desiredQty, this.config.variantId);
    } catch (error) {
      console.error('[cart-surcharge]', error);
    } finally {
      this.#busy = false;
    }
  }

  async applyChange(feeItem, quantity, variantId) {
    // Stesse sezioni che il tema ri-renderizza durante una mutazione del carrello
    const sections = new Set();
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) sections.add(el.dataset.sectionId);
    });
    const sectionsParam = Array.from(sections).join(',');
    const sectionsUrl = window.location.pathname;

    let url;
    let body;
    if (feeItem) {
      url = Theme.routes.cart_change_url;
      body = JSON.stringify({
        id: feeItem.key,
        quantity,
        sections: sectionsParam,
        sections_url: sectionsUrl,
      });
    } else {
      url = Theme.routes.cart_add_url;
      body = JSON.stringify({
        items: [{ id: variantId, quantity }],
        sections: sectionsParam,
        sections_url: sectionsUrl,
      });
    }

    const responseText = await fetch(url, fetchConfig('json', { body })).then((r) => r.text());
    const parsed = JSON.parse(responseText);

    if (parsed.errors || parsed.status >= 400) {
      console.error('[cart-surcharge] aggiornamento fallito', parsed);
      return;
    }

    // Carrello aggiornato per l'evento; le sezioni HTML arrivano dalla risposta
    const cart = await this.getCart();
    const sectionsMap = parsed.sections || cart.sections;

    document.dispatchEvent(
      new CartUpdateEvent(cart, 'cart-surcharge', {
        source: 'cart-surcharge',
        itemCount: cart.item_count,
        sections: sectionsMap,
      })
    );
  }
}

if (!customElements.get('cart-surcharge-manager')) {
  customElements.define('cart-surcharge-manager', CartSurchargeManager);
}
